<template>
  <div>
    <v-data-table
      :headers="headers"
      :items="items"
      sort-by="displayName"
      :search="search"
      class="elevation-0 general-table"
      :loading="isLoading"
      @click:row="rowClick"
      :loading-text="$t('GLOBAL-LOADING-PLEASE_WAIT')"
    >
      <template v-slot:header.image="{ header }">
        <lang t="PACKAGING_CLASSES-TBL-HEADER-IMAGE"></lang>
      </template>

      <template v-slot:header.name="{ header }">
        <lang t="PACKAGING_CLASSES-TBL-HEADER-NAME"></lang>
      </template>

      <template v-slot:header.level="{ header }">
        <lang t="PACKAGING_CLASSES-TBL-HEADER-LEVEL"></lang>
      </template>

      <template v-slot:header.multiscanMode="{ header }">
        <lang t="PACKAGING_CLASSES-TBL-HEADER-MULTISCANMODE"></lang>
      </template>

      <template v-slot:footer.page-text="props" v-if="items.length > 0">
        <display-number :number="props.pageStart" />-<display-number :number="props.pageStop" /> {{ $t("of") }}
        <display-number :number="items.length" />
      </template>

      <template v-slot:top>
        <div class="d-flex align-center flex-wrap py-2 mb-3">
          <v-text-field
            clearable
            class="general-table__search"
            v-model="search"
            prepend-inner-icon="mdi-magnify"
            :label="$t('GLOBAL-DO_SEARCH')"
            solo
            flat
            dense
            hide-details
          ></v-text-field>

          <v-spacer></v-spacer>

          <Button type="outside-label" icon="mdi-plus" :label="$t('PACKAGING_CLASSES-BTN-NEW_PACKAGING_CLASS')" @click="newItem" />
        </div>
      </template>

      <template v-slot:item.image="{ item }">

        <v-img
              :src="$config.cdn + item.image"
              contain
              max-width="120"
              height="32"
            ></v-img>
      </template>

      <template v-slot:item.name="{ item }">
        {{ item.name }}
      </template>

      <template v-slot:item.actions="{ item }">
        <v-icon small class="pa-1" color="grey darken-2" @click.stop.prevent="itemDelete(item)">mdi-trash-can-outline</v-icon>
      </template>

      <template v-slot:no-data>
        <v-btn color="primary" @click="initialize">Reset</v-btn>
      </template>
    </v-data-table>

    <v-dialog v-model="dialog.show" max-width="640px" :persistent="false">
      <v-card>
        <v-card-title>
          <span class="headline"><lang :t="dialog.title"></lang></span>
        </v-card-title>

        <v-card-text>
          <v-text-field :label="$t('PACKAGING_CLASS-LABEL-NAME')" v-model="editedItem.name"></v-text-field>

          <v-row align="center" dense class="ma-0 mb-5 pa-0">
            <v-file-input
              accept="image/png, image/jpeg"
              :placeholder="$t('GLOBAL-FILE_UPLOAD-CHOOSE_FILE')"
              prepend-icon="mdi-image"
              :label="$t('PACKAGING_CLASS-LABEL-FILE_UPLOAD')"
              :loading="isUploading ? 'green' : false"
              :disabled="isUploading"
              @change="imageUpload($event)"
              @click:clear="imageRemove"
              :key="editedItem"
            >
            </v-file-input>

            <v-sheet :key="editedItem" color="grey lighten-3" width="48px" height="36px" class="ml-4" style="display:flex; align-items: center;">
              <v-img max-height="28px" contain :src="getImgSrc()">
                <template v-slot:placeholder>
                  <v-row class="fill-height ma-0" align="center" justify="center">
                    <v-progress-circular size="16" width="2" indeterminate color="grey darken-3"></v-progress-circular>
                  </v-row>
                </template>
              </v-img>
            </v-sheet>
          </v-row>

          <v-row>
            <v-col cols="6">
              <v-select
                :label="$t('PACKAGING_CLASS-LABEL-LEVEL')"
                v-model="editedItem.level"
                :items="levels"
                item-text="name"
                item-value="value"
              ></v-select>
            </v-col>

            <v-col cols="6">
              <v-select
                :label="$t('PACKAGING_CLASS-LABEL-MULTISCANMODE')"
                v-model="editedItem.multiscanMode"
                :items="multiscanModes"
                item-text="name"
                item-value="value"
              ></v-select>
            </v-col>
          </v-row>

          <v-row dense>
            <v-col cols="4">
              <v-switch class="pa-0 ma-0" dense v-model="editedItem.hasRoundBase" :label="$t('PACKAGING_CLASS-LABEL-HAS_ROUND_BASE')"></v-switch>
            </v-col>
            <v-col cols="4">
              <v-switch class="pa-0 ma-0" dense v-model="editedItem.canBeFoiled" :label="$t('PACKAGING_CLASS-LABEL-CAN_BE_FOILED')"></v-switch>
            </v-col>
            <v-col cols="4">
              <v-switch class="pa-0 ma-0" dense v-model="editedItem.isTransparent" :label="$t('PACKAGING_CLASS-LABEL-IS_TRANSPARENT')"></v-switch>
            </v-col>
            <v-col cols="4">
              <v-switch class="pa-0 ma-0" dense v-model="editedItem.isReflecting" :label="$t('PACKAGING_CLASS-LABEL-IS_REFLECTING')"></v-switch>
            </v-col>
            <v-col cols="4">
              <v-switch class="pa-0 ma-0" dense v-model="editedItem.isSuckable" :label="$t('PACKAGING_CLASS-LABEL-IS_SUCKABLE')"></v-switch>
            </v-col>
            <v-col cols="4">
              <v-switch class="pa-0 ma-0" dense v-model="editedItem.isTiltable" :label="$t('PACKAGING_CLASS-LABEL-IS_TILTABLE')"></v-switch>
            </v-col>
          </v-row>

          <v-row dense>
            <v-col cols="4">
              <v-text-field :label="$t('PACKAGING_CLASS-LABEL-FRICTION')" v-model="editedItem.friction"></v-text-field>
            </v-col>
            <v-col cols="4">
              <v-text-field :label="$t('PACKAGING_CLASS-LABEL-STAMP_FORCE')" v-model="editedItem.stampForce"></v-text-field>
            </v-col>

            <v-col cols="4">
              <v-text-field :label="$t('PACKAGING_CLASS-LABEL-OVERHANG_PERCENTAGE')" v-model="editedItem.overhangPercentage"></v-text-field>
            </v-col>
            <v-col cols="4">
              <v-text-field :label="$t('PACKAGING_CLASS-LABEL-BRIDGE_PERCENTAGE')" v-model="editedItem.bridgePercentage"></v-text-field>
            </v-col>
            <v-col cols="4">
              <v-select
                :label="$t('PACKAGING_CLASS-LABEL-DURABILITY_CODE')"
                v-model="editedItem.durabilityCode"
                :items="['D1', 'D2', 'D3', 'D4', 'D5', 'D6', 'D99']"
              ></v-select>
            </v-col>
          </v-row>
        </v-card-text>

        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn text @click="closeModal" :disabled="dialog.isSaving"><lang t="GLOBAL-BTN-CANCEL"></lang></v-btn>
          <v-btn elevation="0" light rounded color="themeColor" :loading="dialog.isSaving" @click="itemSave()"
            ><lang t="GLOBAL-BTN-SAVE"></lang
          ></v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </div>
</template>

<script>
import Button from "../../../components/ui-elements/Button.vue";
import DisplayNumber from "../../../components/DisplayNumber.vue";

export default {
  name: "PackingClassesIndex",
  components: {
    Button,
    DisplayNumber,
  },
  data: () => ({
    isSaving: false,
    isLoading: true,
    isUploading: false,

    search: "",

    headers: [
      { text: "Image", align: "start", value: "image", sortable: false, class: "general-table__th--4" },
      { text: "Name", align: "start", value: "displayName", sortable: true, class: "general-table__th--4" },
      { text: "Level", value: "level", sortable: true, class: "general-table__th--4" },
      { text: "Multiscan Mode", value: "multiscanMode", sortable: true, class: "general-table__th--4" },
      { text: "", value: "actions", sortable: false, width: "80px", align: "end" },
    ],

    items: [],

    dialog: {
      show: false,
      title: "",
      isSaving: false,
    },

    editedIndex: -1,

    editedItem: {
      name: null,
      level: null,
      image: null,
      imageTmp: null,
      multiscanMode: null,
      hasRoundBase: false,
      canBeFoiled: false,
    },

    defaultItem: {
      name: null,
      level: null,
      image: null,
      imageTmp: null,
      multiscanMode: null,
      hasRoundBase: false,
      canBeFoiled: false,
    },
  }),

  watch: {
    "dialog.show"(val) {
      val || this.closeModal();
    },
  },

  computed: {
    levels() {
      return [
        {
          name: this.$t("PACKAGING_CLASS-LEVEL-CASE"),
          value: "case",
        },
        {
          name: this.$t("PACKAGING_CLASS-LEVEL-PACK"),
          value: "pack",
        },
        {
          name: this.$t("PACKAGING_CLASS-LEVEL-UNIT"),
          value: "unit",
        },
      ];
    },

    multiscanModes() {
      return [
        {
          name: this.$t("PACKAGING_CLASS-MULTISCANMODE-COMPLEX"),
          value: "complex",
        },
        {
          name: this.$t("PACKAGING_CLASS-MULTISCANMODE-CUBIC"),
          value: "cubic",
        },
        {
          name: this.$t("PACKAGING_CLASS-MULTISCANMODE-TEXPRES"),
          value: "texpres",
        },
      ];
    },
  },

  created() {
    this.initialize();
  },

  mounted() {},

  methods: {
    getImgSrc() {
      if (this.isUploading) {
        return false;
      }

      let imgUrl = false;

      if (this.editedItem.imageTmp != null) {
        imgUrl = this.editedItem.imageTmp;
      } else {
        imgUrl = this.editedItem.image != null ? this.$config.cdn + this.editedItem.image : false;
      }

      return imgUrl;
    },

    imageRemove() {
      this.editedItem.image = null;
      this.editedItem.imageTmp = null;
      this.$forceUpdate();
    },

    imageUpload(file) {
      if (typeof file == "undefined") {
        return;
      }

      this.isUploading = true;

      let formData = new FormData();
      formData.append("file", file, file.name);

      // additional data
      formData.append("key", "file");

      this.axios
        .post("/upload", formData)
        .then((response) => {
          this.editedItem.image = response.data.filename;
          this.editedItem.imageTmp = response.data.url;

          setTimeout(() => {
            this.isUploading = false;
          }, 1000);
        })
        .catch((error) => {
          console.error(error);
        });
    },

    initialize() {
      this.isLoading = true;

      this.axios.get("bundletypes").then((res) => {
        res.data.forEach((i) => {
          i.displayName = this.$t("bundletypes_" + i.level + "_" + i.name);
        });
        this.items = res.data;
        this.isLoading = false;
      });
    },

    itemDelete(item) {
      this.$confirm(this.$t("GLOBAL-CONFIRMATION-DELETE-MSG", { NAME: item.displayName }), {
        title: this.$t("GLOBAL-CONFIRMATION-DELETE-TITLE"),
        buttonTrueText: this.$t("GLOBAL-CONFIRMATION-DELETE-YES"),
        buttonFalseText: this.$t("GLOBAL-CONFIRMATION-DELETE-CANCEL"),
      }).then((res) => {
        if (res) {
          this.axios.delete("bundletypes/" + item.id).then((res) => {
            this.initialize();
            this.$store.dispatch("setSnackbar", { text: this.$t("GLOBAL-SUCCESSFULLY_DELETED") });
          });
        }
      });
    },

    itemEdit(item) {
      this.dialog.title = this.$t("PACKAGING_CLASSES-EDIT_LHF");
      this.editedIndex = this.items.indexOf(item);
      this.editedItem = Object.assign({}, item);

      this.dialog.show = true;
    },

    itemAdd() {
      this.dialog.title = this.$t("PACKAGING_CLASSES-NEW_PACKAGING_CLASS");
      this.dialog.show = true;
    },

    itemSave() {
      this.dialog.isSaving = true;

      if (this.editedIndex == -1) {
        this.axios
          .post("bundletypes", this.editedItem)
          .then((res) => {
            this._fin();
          })
          .catch((err) => {
            this.dialog.isSaving = false;
            this.$store.dispatch("setSnackbar", { text: this.$t("GLOBAL-MESSAGE-ERROR") + ": " + err.response.data.message, type: "error" });
          });
      } else {
        this.axios
          .put("bundletypes/" + this.editedItem.id, this.editedItem)
          .then((res) => {
            this._fin();
          })
          .catch((err) => {
            this.dialog.isSaving = false;
            this.$store.dispatch("setSnackbar", { text: this.$t("GLOBAL-MESSAGE-ERROR") + ": " + err.response.data.message, type: "error" });
          });
      }
    },

    _fin() {
      this.dialog.isSaving = false;
      this.$store.dispatch("setSnackbar", { text: this.$t("GLOBAL-MESSAGE-SUCCESSFULLY_SAVED") });
      this.initialize();
      this.closeModal();
    },

    closeModal() {
      this.dialog.show = false;

      this.$nextTick(() => {
        this.editedItem = Object.assign({}, this.defaultItem);
        this.editedIndex = -1;
      });
    },

    rowClick(item) {
      this.editItem(item);
    },
    editItem(item) {
      this.$router.push({ name: "settings-packaging_classes-detail", params: { id: item.id, item: item } });
    },
    newItem() {
      this.$router.push({ name: "settings-packaging_classes-detail-new" });
    },
  },
};
</script>

<style lang="scss">
tbody > tr {
  cursor: pointer;
}
</style>
